import { useIsMutating, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePlan } from "@/features/auth/hooks/usePlan";
import { YEATIES_CONSENT_SAVE_ERROR } from "@/features/purchasePoints/config/purchasePoints.config";
import { canBeAskedAboutMesYeaties } from "@/features/purchasePoints/utils/yeatiesConsentEligibility";
import { updateUserLegacy } from "@/features/userProfile/services/userProfile.service";
import { useErrorHandler } from "@/shared/hooks/useErrorHandler";

export type YeatiesConsent = "accepted" | "refused";
export type YeatiesAnswer = "accept" | "refuse";

const YEATIES_CONSENT_MUTATION_KEY = ["yeatiesConsent"];

// Strict comparisons: while the participation is unknown (undefined) nothing is
// asked. Undecided (null) is not taking part yet, so the question is put to him
// as well as to the one who refused.
const isUndecidedOrRefused = (participation: boolean | null | undefined) => participation === false || participation === null;

/**
 * Owns its mutation instead of reusing useUser().updateUserMutation: that one
 * updates the session before the server answers and rolls it back on failure,
 * while the consent below must follow the answer itself. The answer counts from
 * the moment it is sent, so the caller moves on without waiting for the server;
 * a failed save takes it back. A failure is reported through `consentError`,
 * for the caller to react to, and as a toast worded here: the server error
 * alone may carry no title.
 */
export const useYeatiesConsent = () => {
  const { user, updateUserState } = useAuth();
  const { handleError } = useErrorHandler();
  const plan = usePlan();

  const participation = user?.employee?.allow_purchase_points;
  const isNotTakingPart = isUndecidedOrRefused(participation);
  // Counts the answers of every instance: one mounted later must not send it again.
  const isAnswerInFlight = useIsMutating({ mutationKey: YEATIES_CONSENT_MUTATION_KEY }) > 0;

  const answerMutation = useMutation({
    mutationKey: YEATIES_CONSENT_MUTATION_KEY,
    mutationFn: updateUserLegacy,
    // Also when updateUserState runs: useUserQuery keeps ["user"] with an infinite
    // staleTime and replays every write of that cache into the session, so a stale
    // copy would later revert the field.
    meta: { invalidateResources: ["user"] },
    onSuccess: (response) => updateUserState(response.employee),
    // At hook level, not per call: it must survive the unmount of the caller.
    onError: (error) => handleError(error, YEATIES_CONSENT_SAVE_ERROR),
  });

  const answer = (allowPurchasePoints: boolean) => answerMutation.mutate({ allow_purchase_points: allowPurchasePoints });

  const getPendingAnswer = (): YeatiesAnswer | null => {
    if (!answerMutation.isPending) return null;
    return answerMutation.variables?.allow_purchase_points ? "accept" : "refuse";
  };

  const sentConsent: YeatiesConsent = answerMutation.variables?.allow_purchase_points ? "accepted" : "refused";
  const consent = answerMutation.isPending || answerMutation.isSuccess ? sentConsent : null;

  return {
    consent,
    consentError: answerMutation.isError ? YEATIES_CONSENT_SAVE_ERROR.title : null,
    pendingAnswer: getPendingAnswer(),
    // Once answered, a Pusher failure and a second activation must not ask again.
    isOffered: consent === null && !isAnswerInFlight && isNotTakingPart && canBeAskedAboutMesYeaties(user, plan),
    accept: () => answer(true),
    // A refusal already stored is not sent again.
    refuse: () => {
      if (participation !== false) answer(false);
    },
    reset: answerMutation.reset,
  };
};

export type YeatiesConsentState = ReturnType<typeof useYeatiesConsent>;
